import { useCallback, useEffect, useRef, type PropsWithChildren } from 'react'
import { useAuth } from 'react-oidc-context'

import {
  clearAccessToken,
  setAccessToken,
  setUnauthorizedHandler
} from 'src/api/tokenStore'
import { paths } from 'src/router/paths'

const CHANNEL_NAME = 'ebs-intranet-auth'

type AuthEvent = { type: 'SIGNED_OUT' | 'SIGNED_IN' }

export const AuthSideEffects = ({ children }: PropsWithChildren) => {
  const auth = useAuth()

  const authRef = useRef(auth)

  useEffect(() => {
    authRef.current = auth
  })

  useEffect(() => {
    setAccessToken(auth.user?.access_token ?? null)
  }, [auth.user?.access_token])

  const expiringRef = useRef(false)

  const expireSession = useCallback(() => {
    if (expiringRef.current) return

    expiringRef.current = true
    clearAccessToken()
    authRef.current
      .removeUser()
      .finally(() => {
        const here = globalThis.location.pathname + globalThis.location.search
        const returnTo = encodeURIComponent(here)

        globalThis.location.assign(
          `${paths.auth.login}?session=expired&return=${returnTo}`
        )
      })
      .catch(() => undefined)
  }, [])

  useEffect(() => {
    return auth.events.addSilentRenewError(expireSession)
  }, [auth.events, expireSession])

  useEffect(() => {
    setUnauthorizedHandler(expireSession)

    return () => setUnauthorizedHandler(null)
  }, [expireSession])

  useEffect(() => {
    if (!('BroadcastChannel' in globalThis)) return undefined

    const channel = new BroadcastChannel(CHANNEL_NAME)

    const handleMessage = (event: MessageEvent<AuthEvent>) => {
      const current = authRef.current

      if (event.data?.type === 'SIGNED_OUT' && current.isAuthenticated) {
        current.removeUser().catch(() => undefined)
      }

      if (event.data?.type === 'SIGNED_IN' && !current.isAuthenticated) {
        current.signinSilent().catch(() => undefined)
      }
    }

    channel.addEventListener('message', handleMessage)

    const unsubSignedOut = auth.events.addUserSignedOut(() => {
      channel.postMessage({ type: 'SIGNED_OUT' } satisfies AuthEvent)
    })

    const unsubSignedIn = auth.events.addUserLoaded(() => {
      channel.postMessage({ type: 'SIGNED_IN' } satisfies AuthEvent)
    })

    return () => {
      channel.removeEventListener('message', handleMessage)
      channel.close()
      unsubSignedOut()
      unsubSignedIn()
    }
  }, [auth.events])

  return <>{children}</>
}
